import React, { useCallback, useEffect, useMemo, useState } from "react";
import ContactAgentsForm from "./ContactAgentsForm";
import AgentsApi from "../../api-services/agents-api.service";
import { Button, Card, Col, Modal, notification, Row } from "antd";
import { Agent } from "../../interfaces/agent.interface";
import { mdiMapMarker, mdiPhoneInTalkOutline, mdiWhatsapp, mdiEmailOutline, mdiTextureBox } from "@mdi/js";
import Icon from "@mdi/react";
import { uniqBy } from "../../utils/helpers";
import { useSearchParams } from "react-router-dom";

const { Meta } = Card;

interface IContactAgentsProps {
  type: string | string[],
  additionalFilters?: React.ReactNode,
  locationFilterType?: "state-city" | "zone",
  initialValues?: AgentSearchParams
}

interface AgentSearchParams {
  state?: string;
  city?: string;
  fair?: string;
  fullname?: string;
  zone?: string;
  country?: string;
}

const ContactAgents: React.FC<IContactAgentsProps> = ({ type, additionalFilters, locationFilterType = "state-city", initialValues }) => {
  const agentsApi = useMemo(() => new AgentsApi(), []);
  const [loading, setLoading] = useState(true);
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agentData, setAgentData] = useState<Agent>({ name: "", image: "", address: "", phone: "", whatsapp: "", email: "", city: "", state: "", id: 0, username: "" } as Agent);

  const [searchParams] =  useSearchParams();
  const params: AgentSearchParams = {};
  if(searchParams.get("state")){
    params.state = searchParams.get("state") as string;
  }
  if(searchParams.get("city")){
    params.city = searchParams.get("city") as string;
  }
  if(searchParams.get("fair")){
    params.fair = searchParams.get("fair") as string;
  }
  if (searchParams.get("fullname")) { // new
    params.fullname = searchParams.get("fullname") as string;
  }
  if (searchParams.get("zone")) {
    params.zone = searchParams.get("zone") as string;
  }

  const mergedParams: AgentSearchParams = { ...params, ...initialValues };

  const fetchData = useCallback(async (searchParams?: AgentSearchParams) => {
    setLoading(true);
    const params = { departments: type, ...searchParams };
    try {
      const agentsData: Agent[] = await agentsApi.getAgents(params);
      const states = uniqBy(agentsData, "state");
      const cities = uniqBy(agentsData, "city");
      setAgents(agentsData);
      setAllAgents(agentsData);
      setStates(states);
      setCities(cities);
    } catch (error) {
      notification.error({ message: "An error occured while loading agents." });
    } finally {
      setLoading(false);
    }
  }, [agentsApi, type]);

  useEffect(() => {
    fetchData(mergedParams);
  }, [fetchData, mergedParams.country]);

  const showModal = (agent: Agent) => {
    setIsModalOpen(true);
    setAgentData(agent);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const resetAgents = useCallback(() => {
    const states = uniqBy(allAgents, "state");
    const cities = uniqBy(allAgents, "city");
    setStates(states);
    setCities(cities);
  }, [allAgents]);

  const filterCities = (searchStr?: string) => {
    if (searchStr == undefined) {
      resetAgents();
      return;
    }
    const agentsFiltered: Agent[] = allAgents.filter(a => a.state == searchStr);
    const results = uniqBy(agentsFiltered, "city");
    setCities(results);
  };

  const filterStates = (searchStr?: string) => {
    if (searchStr == undefined) {
      resetAgents();
      return;
    }
    const agentsFiltered: Agent[] = allAgents.filter(a => a.city == searchStr);
    const results = uniqBy(agentsFiltered, "state");
    setStates(results);
  };

  return (
    <div className="contact-agents">
      <div className="contact-agents_body">
        <div className="find-agents">
          <div className="find-agents_header"><span>{locationFilterType === "zone" ? "Get Your Zone" : "Get Your City"}</span></div>
          <div className="find-agents_form">
            <ContactAgentsForm
              key={`${locationFilterType}-${mergedParams.country ?? "default"}`}
              initialValues={mergedParams}
              isWidget={false}
              states={states}
              cities={cities}
              locationFilterType={locationFilterType}
              additionalFilters={additionalFilters}
              findAgents={fetchData}
              filterCities={filterCities}
              filterStates={filterStates}
            />
          </div>
        </div>

        <div className="agents-list">
          <Row gutter={[20, 20]}>
            {
              agents.map((agent: Agent) => (
                <Col key={agent.id} xs={24} sm={24} md={12} lg={6}>
                  <Card
                    hoverable
                    cover={<img alt={agent.name} src={agent.image} />}
                    loading={loading}
                  >
                    <Meta title={agent.name} description={
                      <>
                        <div className="agent-details">
                          <Row gutter={[20, 20]} align="middle">
                            <Col span={24}>
                              <Icon path={mdiMapMarker} size="15px" />
                              <span className="agent-address">{agent.address}</span>
                            </Col>
                            <Col span={24}>
                              <Icon path={mdiPhoneInTalkOutline} size="15px" />
                              <span>{agent.phone}</span>
                            </Col>
                            <Col span={24}>
                              <Icon path={mdiWhatsapp} size="15px" />
                              <span>{agent.whatsapp}</span>
                            </Col>
                            <Col span={24}>
                              <Icon path={mdiEmailOutline} size="15px" />
                              <span>{agent.email}</span>
                            </Col>
                            <Col span={24}>
                              <Icon path={mdiTextureBox} size="15px" />
                              <span>{agent.zone}</span>
                            </Col>
                          </Row>
                        </div>
                        <Button color="primary" variant="outlined" className="more-info-button"
                          onClick={() => showModal(agent)}>
                          More info
                        </Button>
                      </>
                    } />
                  </Card>
                </Col>
              ))
            }
          </Row>
        </div>

        <Modal
          title={
            <>
              <img src={agentData?.image} />
              <span> {agentData?.name} </span>
            </>
          }
          open={isModalOpen}
          onCancel={closeModal}
          footer={null}
          width={350}
          centered
          className="agent-full-details"
        >
          <Row gutter={[10, 10]}>
            <Col span={12}> <label>Agent Name</label> </Col>
            <Col span={12}> <span> {agentData?.name} </span></Col>
            <Col span={12}> <label>State</label> </Col>
            <Col span={12}> <span> {agentData?.state} </span></Col>
            <Col span={12}> <label>Address</label> </Col>
            <Col span={12}> <span> {agentData?.address} </span></Col>
            <Col span={12}> <label>Tel</label> </Col>
            <Col span={12}> <span> {agentData?.phone} </span></Col>
            <Col span={12}> <label>Email</label> </Col>
            <Col span={12}> <span> {agentData?.email} </span></Col>
          </Row>
          {/* google maps */}
        </Modal>
      </div>
    </div>
  );
};

export default ContactAgents;